import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import videoBgIcon from '@/images/agents/video_bg_icon.svg';
import theme from '@/theme';
import { Presenter } from '@d-id/types';
import { Button } from '@de-id/ui-components';
import styled from '@emotion/styled';
import { useMemo } from 'react';

const VIDEO_BG = 'video_bg';

const Container = styled.div`
    display: flex;
    padding: 8px;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 8px;
    width: 100%;
    height: 100%;
    align-self: stretch;
    border-radius: 8px;
`;

const Title = styled.div`
    color: #494745;
    font-family: Manrope;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 16px;
`;

const ThumbnailContainer = styled.div`
    display: flex;
    gap: 8px;
    flex-direction: row;
    justify-content: space-between;
`;

const Thumbnail = styled.div<{ src: string; selected: boolean }>`
    border-radius: 14px;
    width: 108px;
    border: 1px solid #dbdbdb;
    aspect-ratio: 1;
    object-fit: cover;
    background: url(${({ src }) => src}) no-repeat center center transparent;
    position: relative;
    background-size: cover;

    &:hover {
        cursor: pointer;
        border: 1px solid ${theme.colors.primary.main};
    }
`;

const VideoBgIconContainer = styled.div`
    position: absolute;
    top: 8px;
    right: 8px;
    border-radius: 50%;
    opacity: 0.8;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${theme.colors.black[800]};
`;

const CustomizeButton = styled(Button)`
    position: absolute;
    bottom: 8px;
    left: 6px;
    max-width: 96px;

    color: ${theme.colors.secondary.main};
    background-color: ${theme.colors.white};
    text-align: center;

    font-family: Manrope;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 20px;
`;

interface Props {
    presenters: Presenter[];
    selectedPresenter: Presenter | undefined;
    onThumbnailClick?: (e: React.MouseEvent<HTMLDivElement>, presenter: Presenter) => void;
    openBackgroundsModal?: (e: React.MouseEvent<HTMLButtonElement>, presenter: Presenter) => void;
}

export const DvpSubSelection = ({ presenters, onThumbnailClick, selectedPresenter, openBackgroundsModal }: Props) => {
    const options = useMemo(() => presenters.filter(presenter => presenter.thumbnail), [presenters]);

    const handleCustomizeClick = (e: React.MouseEvent<HTMLButtonElement>, presenter: Presenter) => {
        e.stopPropagation();
        openBackgroundsModal?.(e, presenter);
    };

    return (
        <Container>
            <Title>Choose or customize background</Title>
            <ThumbnailContainer>
                {options.map(presenter => (
                    <Thumbnail
                        key={presenter.id}
                        src={presenter.thumbnail}
                        onClick={e => onThumbnailClick?.(e, presenter)}
                        selected={presenter?.id === selectedPresenter?.id}>
                        {presenter.id.includes(VIDEO_BG) && (
                            <VideoBgIconContainer>
                                <MaskedIcon size="12px" color="white" src={videoBgIcon} />
                            </VideoBgIconContainer>
                        )}

                        {presenter.is_greenscreen && (
                            <CustomizeButton color="outlined" size="small" onClick={e => handleCustomizeClick(e, presenter)}>
                                Customize
                            </CustomizeButton>
                        )}
                    </Thumbnail>
                ))}
            </ThumbnailContainer>
        </Container>
    );
};
